import {
  faBolt,
  faHand,
  faEdit,
  faTrashAlt,
} from '@fortawesome/pro-regular-svg-icons'
import { TransactionOrSplit } from '@/reducers/admin/allTransactions.slice'
import { useReselector, useToggle } from '@/utils/sharedHooks'
import { DATE_FORMATS_LUXON, isoToUTCDateTime } from '@/utils/dateHelpers'
import {
  selectJournalEntryTransactionById,
  selectJournalEntrySplitTransactionById,
} from '@/selectors/journalEntrySelectors'
import { Card, Text, Label, Button, CardColors, Icon } from '@/ui'
import { TypographyStyles } from '@/styles'

import { JournalEntryLinesTable } from './JournalEntryTransactionRow'
import { EditJournalEntryModal } from './EditJournalEntryModal'
import { DeleteJournalEntryModal } from './DeleteJournalEntryModal'

import { JournalEntry } from '../types'

// TODO: Migrate to v2
import {
  JournalEntryUpdateType,
  MaybeJournalEntry,
} from '@/features/admin/paginated-transaction-table/RowDrawer'
export const TransactionJournalEntriesCard = ({
  journalEntry,
  onUpdate = () => undefined,
}: {
  journalEntry: JournalEntry
  onUpdate?: (
    journalEntry: MaybeJournalEntry,
    type: JournalEntryUpdateType
  ) => void
}) => {
  const [isEditing, toggleEdit] = useToggle()
  const [isDeleting, toggleDelete] = useToggle()
  const { journalEntryDate, details, automated, userId, journalEntryLines } =
    journalEntry

  return (
    <Card color={CardColors.LEVEL_0} className="p-4">
      <div className="flex flex-col md:flex-row gap-6">
        {/* Left Column */}
        <div className="w-full md:w-1/3">
          <Text variant={TypographyStyles.H3} className="mb-4">
            {isoToUTCDateTime(journalEntryDate).toFormat(
              DATE_FORMATS_LUXON.INPUT
            )}
          </Text>
          <Text variant={TypographyStyles.BODY_SM} className="mb-4 italic">
            {details}
          </Text>
          {automated ? (
            <Label className="mb-4">
              <Icon icon={faBolt} />
              Auto
            </Label>
          ) : (
            <Label className="mb-4">
              <Icon icon={faHand} />
              Manual
            </Label>
          )}

          <div className="flex gap-4 mt-4">
            <Button variant="discreet" onClick={toggleEdit} iconLeft={faEdit} />
            {!automated && (
              <Button
                variant="discreet"
                onClick={toggleDelete}
                iconLeft={faTrashAlt}
              />
            )}
          </div>
        </div>

        {/* Right Column */}
        <div className="w-full md:w-2/3">
          <JournalEntryLinesTable
            journalEntryLines={journalEntryLines}
            userId={userId}
          />
        </div>
      </div>

      {isEditing && (
        <EditJournalEntryModal
          open={isEditing}
          onClose={toggleEdit}
          onUpdate={onUpdate}
          journalEntry={journalEntry}
        />
      )}
      {isDeleting && (
        <DeleteJournalEntryModal
          open={isDeleting}
          onClose={toggleDelete}
          journalEntry={journalEntry}
          onUpdate={onUpdate}
          userId={userId}
        />
      )}
    </Card>
  )
}

export const TransactionJournalEntriesPanel = ({
  transaction,
  ignoreRedux = false,
  onUpdate = () => undefined,
}: {
  transaction: TransactionOrSplit
  ignoreRedux?: boolean
  onUpdate?: (
    journalEntry: MaybeJournalEntry,
    type: JournalEntryUpdateType
  ) => void
}) => {
  const [isCreating, toggleCreate] = useToggle()

  const journalEntryTransaction = useReselector(
    selectJournalEntryTransactionById,
    transaction.id
  )
  const splitJournalEntryTransaction = useReselector(
    selectJournalEntrySplitTransactionById,
    transaction.id,
    transaction.splitFrom
  )

  const foundTransaction = transaction.splitFrom
    ? splitJournalEntryTransaction
    : journalEntryTransaction

  const journalEntries =
    (ignoreRedux
      ? (transaction.journalEntries as JournalEntry[])
      : foundTransaction?.journalEntries) ?? []

  if (!foundTransaction && !ignoreRedux) return null

  return (
    <div className="p-4">
      <div className="flex justify-end mb-4">
        <Button
          variant="secondary"
          onClick={toggleCreate}
          size="sm"
          className="mt-2"
        >
          Create Journal Entry
        </Button>
      </div>

      <div className="space-y-4">
        {journalEntries.map((journalEntry) => (
          <TransactionJournalEntriesCard
            journalEntry={journalEntry}
            key={journalEntry.id}
            onUpdate={onUpdate}
          />
        ))}
      </div>

      {isCreating && (
        <EditJournalEntryModal
          open={isCreating}
          onClose={toggleCreate}
          onUpdate={onUpdate}
          transactionId={transaction.id.toString()}
        />
      )}
    </div>
  )
}
